import { useState } from 'react';

import Collapsible from 'react-collapsible';
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import type { TotadminPermission } from 'data/__generated__';
import { useMissingPermissionsReason, usePermissionGate } from 'hooks/useHasPermission';
import { PermissionLockIcon } from 'components/PermissionLock';

export const SectionContainer = ({
    children,
    title,
    isInitiallyOpen = false,
    keepOpen = false,
    lazy = false,
    linkToMore,
    linkToMoreLabel = 'voir plus',
    requiredPermissions,
}: {
    children: JSX.Element;
    title: JSX.Element | string;
    isInitiallyOpen?: boolean;
    keepOpen?: boolean;
    // Ne monte le contenu qu'à la première ouverture. Par défaut la section est repliée mais montée : sa
    // requête part quand même au chargement de la page. À activer sur les sections coûteuses (une liste
    // paginée, un graphe) qu'on n'ouvre qu'occasionnellement.
    lazy?: boolean;
    linkToMore?: string;
    linkToMoreLabel?: string;
    /**
     * Droits fins TOUS requis pour afficher la section. Sans eux : repliée, verrouillée, et son contenu
     * n'est PAS monté — ses requêtes ne partent pas, donc pas d'« Accès refusé » en cascade.
     */
    requiredPermissions?: readonly TotadminPermission[];
}) => {
    const { isAllowed, missing } = usePermissionGate(requiredPermissions);
    const reason = useMissingPermissionsReason(missing);
    const [isOpen, setIsOpen] = useState(keepOpen || isInitiallyOpen);
    // Une fois ouverte, la section reste montée : replier ne doit pas jeter ce qui a été chargé.
    const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);

    if (!isAllowed) {
        return (
            <CollapsibleContainer data-test="section-locked" title={reason ?? undefined}>
                <CollapsibleHeader $isLocked>
                    <SectionContainerTitle>{title}</SectionContainerTitle>
                    <LockedReason>
                        <PermissionLockIcon className="size-4" />
                        {reason}
                    </LockedReason>
                </CollapsibleHeader>
            </CollapsibleContainer>
        );
    }

    return (
        <CollapsibleContainer>
            <Collapsible
                trigger={
                    <CollapsibleHeader
                        onClick={() => {
                            if (keepOpen) return;

                            if (!isOpen) setHasBeenOpened(true);
                            setIsOpen(!isOpen);
                        }}
                    >
                        <SectionContainerTitle>
                            {title}
                            {linkToMore ? <SeeMoreLink to={linkToMore}>{linkToMoreLabel}</SeeMoreLink> : null}
                        </SectionContainerTitle>
                        {!keepOpen ? (
                            <IconContainer>
                                {isOpen ? <BsCaretUpFill size="16" /> : <BsCaretDownFill size="16" />}
                            </IconContainer>
                        ) : null}
                    </CollapsibleHeader>
                }
                transitionTime={200}
                triggerDisabled={keepOpen}
                open={isOpen}
                overflowWhenOpen="visible"
                openedClassName="collapsible-opened"
                triggerClassName="collapsible-trigger-closed"
                triggerOpenedClassName="collapsible-trigger-opened"
                contentInnerClassName="collapsible-content-inner"
            >
                {!lazy || hasBeenOpened ? children : <div />}
            </Collapsible>
        </CollapsibleContainer>
    );
};

const SectionContainerTitle = styled.h2`
    margin: 0;
    font-size: 20px;
`;

const CollapsibleContainer = styled.div`
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-radius: var(--radius-lg);
    border: 1px solid ${({ theme }) => theme.lightBorderColor};

    .Collapsible {
        &__contentInner {
            border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
            padding: 15px;
        }
    }
`;

const CollapsibleHeader = styled.div<{ $isLocked?: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0 15px 15px;
    font-size: 24px;
    color: ${({ theme, $isLocked }) => ($isLocked ? theme.disabledTextColor : theme.textColor)};
    cursor: ${({ $isLocked }) => ($isLocked ? 'not-allowed' : 'pointer')};
`;

const LockedReason = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 15px;
    font-size: 13px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const IconContainer = styled.div`
    padding: 0 15px;
`;

const SeeMoreLink = styled(Link)`
    margin-left: 15px;
    font-size: 18px;
    color: ${({ theme }) => theme.textColor};
`;
